import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  type SortingState,
  type TableOptions,
  type TableState,
  useReactTable,
} from "@tanstack/react-table";
import clsx from "clsx";
import { useMemo } from "react";
import { StringParam, useQueryParam, withDefault } from "use-query-params";

import {
  Table,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "common/components/ui/Table";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  isLoading: boolean;
  onRowClick?: (rowValue: TData) => void;
}

export default function MobileListTable<TData, TValue>({
  columns,
  data,
  onRowClick = null,
  isLoading,
}: DataTableProps<TData, TValue>) {
  const is2xlDesktop = useTwBreakpoint("2xl");

  const [sortBy, setSortBy] = useQueryParam(
    "sortBy",
    withDefault(StringParam, ""),
    {
      removeDefaultsFromUrl: true,
      updateType: "replaceIn",
    },
  );

  const [sortOrder, setSortOrder] = useQueryParam(
    "sortOrder",
    withDefault(StringParam, ""),
    {
      removeDefaultsFromUrl: true,
      updateType: "replaceIn",
    },
  );

  const sorting: SortingState = useMemo(() => {
    if (!sortBy || !sortOrder) return [];

    return [{ id: sortBy, desc: sortOrder === "desc" }];
  }, [sortBy, sortOrder]);

  const pageSize = is2xlDesktop ? 14 : 7;

  const state: Partial<TableState> = {
    sorting,
  };

  const reactTableOptions: TableOptions<TData> = {
    data: isLoading || !data ? new Array(pageSize).fill({}) : data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: (updaterOrValue) => {
      const newSorting =
        typeof updaterOrValue === "function"
          ? updaterOrValue(sorting)
          : updaterOrValue;

      if (newSorting.length === 0) {
        // Clear sorting
        setSortBy("");
        setSortOrder("");
      } else {
        // Set new sorting
        const sort = newSorting[0];
        setSortBy(sort.id);
        setSortOrder(sort.desc ? "desc" : "asc");
      }
    },
    getSortedRowModel: getSortedRowModel(),
    autoResetPageIndex: false,
    state,
  };

  const { getHeaderGroups, getRowModel, getRowCount, firstPage } =
    useReactTable(reactTableOptions);

  if (getRowModel().rows.length === 0 && getRowCount() !== 0 && !isLoading) {
    firstPage();
  }

  return (
    <Table className="mb-auto -mt-2.5">
      <TableHeader>
        {getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <TableHead key={header.id}>
                {header.isPlaceholder
                  ? null
                  : flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
              </TableHead>
            ))}
          </tr>
        ))}
      </TableHeader>
      <tbody>
        {getRowModel().rows.map((row) => (
          <TableRow
            onClick={() => {
              if (onRowClick) {
                onRowClick(row.original);
              }
            }}
            key={row.id}
            data-state={row.getIsSelected() && "selected"}
            className={clsx({
              "hover:shadow-none hover:cursor-auto": !onRowClick,
            })}
          >
            {row.getVisibleCells().map((cell) => (
              <TableCell key={cell.id}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </tbody>
    </Table>
  );
}
